import { useCallback, useEffect, useState } from 'react';
import { Copy, Plus, Trash } from '@phosphor-icons/react';
import api from '../lib/api';
import { extractErrorMessage } from '../lib/errors';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import ConfirmDialog from './ConfirmDialog';
import { useT } from '../i18n/I18nContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const EXPIRY_OPTIONS = ['30', '90', '365', 'never'];

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * The signed-in user's personal access tokens (``cmr_pat_…``). Unlike project
 * API keys, one token reaches every project the user is a member of and acts
 * as the user — meant for their own scripts and AI coding agents.
 */
export default function PersonalTokensPanel() {
  const t = useT();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState('90');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/integrations/tokens');
      setTokens(data);
    } catch (err) {
      toast.error(extractErrorMessage(err, t('personalTokens.loadFailedToast')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const createToken = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post('/integrations/tokens', {
        name: name.trim(),
        expires_in_days: expiry === 'never' ? null : Number(expiry),
      });
      setCreated(data);
      setName('');
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, t('personalTokens.createFailedToast')));
    } finally {
      setCreating(false);
    }
  };

  const copyToken = async () => {
    if (!created?.token) return;
    try {
      await navigator.clipboard.writeText(created.token);
      toast.success(t('settings.apiKeys.copiedToast'));
    } catch {
      toast.error(t('settings.apiKeys.copyFailedToast'));
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await api.delete(`/integrations/tokens/${revokeTarget.id}`);
      toast.success(t('personalTokens.revokedToast'));
      setRevokeTarget(null);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, t('personalTokens.revokeFailedToast')));
      throw err;
    } finally {
      setRevoking(false);
    }
  };

  const apiBase = `${BACKEND_URL || window.location.origin}/api`;

  return (
    <>
      <ConfirmDialog
        open={!!revokeTarget}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        title={t('personalTokens.revokeConfirmTitle')}
        description={t('personalTokens.revokeConfirmDescription', { name: revokeTarget?.name || '' })}
        confirmLabel={t('personalTokens.revokeConfirmBtn')}
        cancelLabel={t('common.cancel')}
        variant="destructive"
        loading={revoking}
        onConfirm={confirmRevoke}
      />
      <Dialog open={!!created} onOpenChange={(open) => !open && setCreated(null)}>
        <DialogContent className="rounded-sm sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display font-black tracking-tight">
              {t('personalTokens.createdDialogTitle')}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {t('settings.apiKeys.createdDialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input readOnly value={created?.token || ''} className="rounded-sm font-mono text-xs" />
              <Button type="button" variant="outline" className="rounded-sm shrink-0" onClick={copyToken}>
                <Copy size={14} />
              </Button>
            </div>
            <pre className="text-[10px] font-mono text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-sm p-2 overflow-x-auto whitespace-pre-wrap">
              {t('personalTokens.example', { base: apiBase })}
            </pre>
          </div>
          <DialogFooter>
            <Button type="button" className="rounded-sm" onClick={() => setCreated(null)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-3">
        <p className="text-xs text-zinc-500">{t('personalTokens.description')}</p>

        <form onSubmit={createToken} className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-wider font-semibold">{t('settings.apiKeys.nameLabel')}</Label>
            <Input
              data-testid="pat-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('personalTokens.namePlaceholder')}
              className="rounded-sm mt-1"
              maxLength={64}
            />
          </div>
          <div className="sm:w-32">
            <Label className="text-xs uppercase tracking-wider font-semibold">{t('personalTokens.expiryLabel')}</Label>
            <Select value={expiry} onValueChange={setExpiry}>
              <SelectTrigger data-testid="pat-expiry" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EXPIRY_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt === 'never' ? t('personalTokens.expiryNever') : t('personalTokens.expiryDays', { days: opt })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="submit"
            data-testid="pat-create-btn"
            disabled={creating || !name.trim()}
            className="rounded-sm bg-black text-white hover:bg-zinc-800 shrink-0"
          >
            <Plus size={14} className="mr-1" />
            {creating ? t('settings.apiKeys.creatingBtn') : t('personalTokens.createBtn')}
          </Button>
        </form>

        {loading ? (
          <p className="text-xs text-zinc-500">{t('common.loading')}</p>
        ) : tokens.length === 0 ? (
          <p className="text-xs text-zinc-500">{t('personalTokens.empty')}</p>
        ) : (
          <ul className="space-y-2">
            {tokens.map((tok) => (
              <li
                key={tok.id}
                className="flex items-center justify-between gap-3 rounded-sm border border-zinc-200 bg-white px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{tok.name}</p>
                  <p className="text-[10px] font-mono text-zinc-500">
                    {tok.key_prefix}… · {t('settings.apiKeys.createdAt', { when: formatWhen(tok.created_at) })}
                    {tok.last_used_at ? ` · ${t('settings.apiKeys.lastUsedAt', { when: formatWhen(tok.last_used_at) })}` : ''}
                    {' · '}
                    {tok.expires_at
                      ? t('personalTokens.expiresAt', { when: formatWhen(tok.expires_at) })
                      : t('personalTokens.noExpiry')}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="rounded-sm text-red-600 hover:bg-red-50 hover:text-red-700 shrink-0"
                  onClick={() => setRevokeTarget(tok)}
                >
                  <Trash size={14} />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
